import {
  BACKLOG_CAP, GOODS, TRUCK_SPEED, WORLD_H, WORLD_W,
} from "../core/constants";
import {
  clamp, closedRoute, congestionFactor, corridorLoad, distance, findNode, findRoute,
  corridorOf, hasMilestone, routeCost, routeLanes, routePoints, totalBacklog, totalStock,
} from "../core/sim";
import type { GameNode, GameState, Route, WorldRect } from "../core/types";
import { wantedGoods, worldRect } from "../core/world";
import type { UiState } from "../ui/state";
import { FLOAT_LIFETIME } from "../ui/state";
import { goodColor, palette } from "./palette";
import { clampView, fitScale, fitView, toWorld } from "./view";
import type { Point, View } from "./view";

const NODE_R = 27;
const RING_GAP = 7;
/**
 * The dots above a city that say which goods it eats (RFC-0016 §3.9).
 *
 * Purely visual, hence here and not in constants.ts — nothing about the
 * simulation depends on them, and they carry no balancing value. The gap is the
 * spacing between two dots; a row of them stays centred on the node, so a second
 * good widens it symmetrically instead of pushing it off to one side.
 */
const GOOD_DOT_R = 4;
const GOOD_DOT_GAP = 11;
/** Trefferradius für Knoten, etwas großzügiger als der sichtbare Kreis. */
export const NODE_HIT_R = 33;
export const ROUTE_HIT_R = 11;

/**
 * How a congestion factor reads on screen. A display decision, so it lives in
 * the render layer with the trust thresholds further down — and in one place,
 * because the map and the panel have to agree or the player gets two answers.
 *
 * "clear" is not `factor === 1`. A route at capacity runs at 98 % on the start
 * map, and painting that as a jam would cry wolf on exactly the fleet size
 * competent play settles on. Below a tenth of a loss is noise; the real signal
 * starts at the first truck past capacity, which is 55 % (RFC-0003 §3.6).
 */
export const jamLevel = (factor: number): "clear" | "warn" | "bad" =>
  factor > 0.9 ? "clear" : factor > 0.5 ? "warn" : "bad";

/**
 * How wide a road is drawn, in world units — capped at its own click target.
 *
 * A wider road, not n roads: the dashed goods line stays the centre line, and
 * two strokes 3 world-px apart are one blurred stroke on a phone at fit zoom.
 *
 * The cap is `2 × ROUTE_HIT_R` and is derived rather than typed, because the two
 * numbers are the same promise seen from both sides: asphalt the player can see
 * has to be asphalt the player can hit. Without it `lineWidth` grew 3 per lane
 * forever and passed the hit corridor at six lanes (#65) — RFC-0014 §3.6
 * deliberately has no `MAX_LANES`, so the drawing is what stops, not the
 * mechanic.
 */
export const routeWidth = (lanes: number, active: boolean): number =>
  Math.min(2 * ROUTE_HIT_R, (active ? 8 : 6) + 3 * (lanes - 1));

/**
 * How far apart two roads on the same pair are drawn, in world units (#108).
 *
 * Visual, so it lives here rather than in constants.ts: nothing in the
 * simulation knows a road has a sideways position. Two goods out of one hub into
 * one city are two roads with the same course — `buildRoute()` allows the pair
 * again as soon as the good differs — and drawn on the centre line they are one
 * road on screen, priced and staffed twice.
 *
 * 9 is the widest the corridor tolerates before the shift outgrows what a phone
 * at fit zoom can resolve at all, and it clears an ordinary 6-wide road with a
 * gap the eye reads as two.
 */
export const ROUTE_SPREAD = 9;

/**
 * The sideways shift of every road that shares its node pair, by route id.
 *
 * Keyed by the *unordered* pair: hub → hub can be built both ways round, and the
 * two roads share one strip of asphalt whichever direction they are read in.
 * Roads that have their pair to themselves are left out entirely, so the common
 * map pays one pass and no entries.
 */
const pairKey = (a: number, b: number): string => (a < b ? `${a}:${b}` : `${b}:${a}`);

/** Ranks things that lie on the same stretch, symmetrically about its centre. */
function spread(groups: Map<string, number[]>): Map<number, number> {
  const out = new Map<number, number>();
  for (const ids of groups.values()) {
    if (ids.length < 2) continue;
    // By id, so a road keeps its side for as long as it exists — a shift that
    // reshuffles when a neighbour is sold is worse than no shift at all.
    ids.sort((a, b) => a - b);
    for (let i = 0; i < ids.length; i++) {
      out.set(ids[i]!, (i - (ids.length - 1) / 2) * ROUTE_SPREAD);
    }
  }
  return out;
}

/**
 * Where each corridor is drawn, sideways.
 *
 * Grouped by node pair rather than by id, because two *parallel* corridors
 * between the same two nodes are the same stretch on screen — and one of them
 * is the escape hatch RFC-0033 I1 promises. Drawn on top of each other it would
 * look like the player bought nothing.
 */
export function corridorOffsets(s: GameState): Map<number, number> {
  const groups = new Map<string, number[]>();
  for (const c of s.corridors) {
    const key = pairKey(c.from, c.to);
    const seen = groups.get(key);
    if (seen) seen.push(c.id); else groups.set(key, [c.id]);
  }
  return spread(groups);
}

/**
 * Where each route's goods line is drawn, sideways.
 *
 * By the node pair of its **first** corridor, which for a direct road is its
 * own two ends — so this is what it always was, plus the case RFC-0033 PR 3
 * adds: two routes over one shared stretch, which have to be told apart.
 */
export function routeOffsets(s: GameState): Map<number, number> {
  const groups = new Map<string, number[]>();
  for (const r of s.routes) {
    const first = r.corridors[0];
    if (first === undefined) continue;
    const c = corridorOf(s, first);
    const key = pairKey(c.from, c.to);
    const seen = groups.get(key);
    if (seen) seen.push(r.id); else groups.set(key, [r.id]);
  }
  return spread(groups);
}

/** The polyline a route is drawn along, offset so neighbours stay readable. */
export function routePath(
  s: GameState, r: Route, offset: number,
): { x: number; y: number }[] {
  const nodes = routePoints(s, r);
  if (nodes.length === 2) {
    const { ax, ay, bx, by } = routeLine(
      nodes[0]!, nodes[1]!, distance(nodes[0]!, nodes[1]!), offset,
    );
    return [{ x: ax, y: ay }, { x: bx, y: by }];
  }
  // A path is offset segment by segment. The corner then has a small notch,
  // which is honest: the two legs are two roads and the truck really does stop
  // turning there.
  const out: { x: number; y: number }[] = [];
  for (let i = 0; i + 1 < nodes.length; i++) {
    const a = nodes[i]!, b = nodes[i + 1]!;
    const { ax, ay, bx, by } = routeLine(a, b, distance(a, b), offset);
    if (i === 0) out.push({ x: ax, y: ay });
    out.push({ x: bx, y: by });
  }
  return out;
}

/** Where along that polyline a truck at `pos` sits, and which way it faces. */
export function pointOnPath(
  path: { x: number; y: number }[], pos: number,
): { x: number; y: number; ux: number; uy: number } {
  const spans = path.slice(1).map((p, i) => Math.hypot(p.x - path[i]!.x, p.y - path[i]!.y));
  const total = spans.reduce((a, b) => a + b, 0);
  let want = Math.max(0, Math.min(1, pos)) * total;
  for (let i = 0; i < spans.length; i++) {
    const len = spans[i]!;
    if (want > len && i < spans.length - 1) { want -= len; continue; }
    const a = path[i]!, b = path[i + 1]!;
    const t = len === 0 ? 0 : Math.min(1, want / len);
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t,
      ux: (b.x - a.x) / (len || 1), uy: (b.y - a.y) / (len || 1) };
  }
  const last = path[path.length - 1]!;
  return { x: last.x, y: last.y, ux: 1, uy: 0 };
}

/**
 * The two ends of a road as it is drawn — and, at the same offset, as it is hit.
 * Both sides have to use this or the map shows asphalt the pointer misses (#65).
 */
function routeLine(
  a: Point, b: Point, length: number, offset: number,
): { ax: number; ay: number; bx: number; by: number } {
  if (offset === 0) return { ax: a.x, ay: a.y, bx: b.x, by: b.y };
  const nx = -(b.y - a.y) / length * offset;
  const ny = (b.x - a.x) / length * offset;
  return { ax: a.x + nx, ay: a.y + ny, bx: b.x + nx, by: b.y + ny };
}

export class MapRenderer {
  private ctx: CanvasRenderingContext2D;
  /** Replaced wholesale by resize(); the 1×1 is only a value to start from. */
  private view: View = fitView(1, 1);
  /** Viewport size in CSS px, and the device pixel ratio it is drawn at. */
  private vw = 1;
  private vh = 1;
  private dpr = 1;
  private bounds: WorldRect = { x: 0, y: 0, width: WORLD_W, height: WORLD_H };

  constructor(private readonly cv: HTMLCanvasElement) {
    const ctx = cv.getContext("2d");
    if (!ctx) throw new Error("2D-Kontext nicht verfügbar");
    this.ctx = ctx;
    this.resize();
  }

  /**
   * Re-fits to the new viewport. Zoom and centre survive a resize — a mobile
   * browser retracting its URL bar must not throw the player's view away — and
   * are only moved as far as the clamp requires.
   */
  resize(): void {
    const rect = this.cv.parentElement?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return;
    this.dpr = window.devicePixelRatio || 1;
    this.vw = rect.width;
    this.vh = rect.height;
    this.cv.width = Math.round(rect.width * this.dpr);
    this.cv.height = Math.round(rect.height * this.dpr);
    this.view = clampView({ ...this.view, fit: fitScale(this.vw, this.vh) }, this.vw, this.vh, this.bounds);
  }

  getView(): View {
    return this.view;
  }

  /** Clamped on the way in, so no caller can place the camera off the board. */
  setView(v: View): void {
    this.view = clampView(v, this.vw, this.vh, this.bounds);
  }

  getWorldRect(): WorldRect { return this.bounds; }

  setWorldRect(bounds: WorldRect): void {
    this.bounds = bounds;
    this.setView(this.view);
  }

  /** Viewport size in CSS px, for input that has to reason in screen space. */
  size(): { vw: number; vh: number } {
    return { vw: this.vw, vh: this.vh };
  }

  /** Bildschirmkoordinate (relativ zum Canvas) → Weltkoordinate. */
  toWorld(cx: number, cy: number): Point {
    return toWorld(this.view, this.vw, this.vh, cx, cy);
  }

  draw(s: GameState, ui: UiState): void {
    this.setWorldRect(worldRect(s));
    const { ctx } = this;
    const p = palette();
    const v = this.view;
    const scale = v.fit * v.zoom;

    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.fillStyle = p.canvasBg;
    ctx.fillRect(0, 0, this.cv.width, this.cv.height);
    ctx.setTransform(
      scale * this.dpr, 0, 0, scale * this.dpr,
      (this.vw / 2 - v.cx * scale) * this.dpr,
      (this.vh / 2 - v.cy * scale) * this.dpr,
    );

    this.drawGrid(p.grid);
    this.drawPendingLink(s, ui);
    this.drawRoutes(s, ui);
    this.drawTrucks(s);
    this.drawNodes(s, ui);
    this.drawFloats(ui);
  }

  // ── Ebenen ───────────────────────────────────────────────────────────────

  private drawGrid(color: string): void {
    const { ctx } = this;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    for (let x = this.bounds.x; x <= this.bounds.x + this.bounds.width; x += 50) {
      ctx.beginPath(); ctx.moveTo(x, this.bounds.y); ctx.lineTo(x, this.bounds.y + this.bounds.height); ctx.stroke();
    }
    for (let y = this.bounds.y; y <= this.bounds.y + this.bounds.height; y += 50) {
      ctx.beginPath(); ctx.moveTo(this.bounds.x, y); ctx.lineTo(this.bounds.x + this.bounds.width, y); ctx.stroke();
    }
  }

  private drawPendingLink(s: GameState, ui: UiState): void {
    if (ui.linkFrom === null) return;
    const { ctx } = this;
    const p = palette();
    const from = findNode(s, ui.linkFrom);
    const hovered = ui.hover !== null ? findNode(s, ui.hover) : null;
    // Anything that can take goods: a city or another hub, but never the node
    // the link started at.
    const valid = hovered !== null && hovered.kind !== "producer" && hovered.id !== ui.linkFrom;
    const target = valid ? hovered : ui.mouse;

    ctx.strokeStyle = valid ? p.coral : p.route;
    ctx.lineWidth = 3;
    ctx.setLineDash([9, 7]);
    ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(target.x, target.y); ctx.stroke();
    ctx.setLineDash([]);

    if (valid) {
      const cost = routeCost(from, hovered);
      this.pill(
        (from.x + hovered.x) / 2,
        (from.y + hovered.y) / 2 - 14,
        `${cost.toLocaleString("de-DE")} €`,
        s.money >= cost ? p.ink : p.error,
      );
    }
  }

  private drawRoutes(s: GameState, ui: UiState): void {
    const { ctx } = this;
    const p = palette();
    // One pass over the fleet, as step() does, rather than a filter per route:
    // a full network is ~90 routes and ~100 trucks, and the quadratic version
    // costs half a million comparisons a second for a colour.
    // Per corridor, not per route: the colour has to be the jam the trucks
    // actually feel, and since RFC-0033 that belongs to the section.
    const load = corridorLoad(s);
    const jamsUnlocked = hasMilestone(s, "verkehr");
    // Once per frame, not once per route: closedRoute() sorts the route list.
    const closedId = closedRoute(s);
    const offsets = routeOffsets(s);

    // The asphalt is drawn per **corridor**, once, because that is what it is:
    // two routes over one stretch are one road, and painting it twice would
    // hide exactly the thing RFC-0033 added. Its colour is its own jam.
    const selected = ui.selection?.kind === "route"
      ? findRoute(s, ui.selection.id)?.corridors ?? []
      : [];
    ctx.lineCap = "round";
    const roadOffsets = corridorOffsets(s);
    for (const c of s.corridors) {
      const a = findNode(s, c.from);
      const b = findNode(s, c.to);
      const active = selected.includes(c.id);
      const level = jamLevel(congestionFactor(c.length, load.get(c.id) ?? 0, jamsUnlocked, c.lanes));
      const jam = level === "clear" ? p.route : level === "warn" ? p.warning : p.error;
      const { ax, ay, bx, by } = routeLine(a, b, c.length, roadOffsets.get(c.id) ?? 0);
      ctx.strokeStyle = active ? p.coral : jam;
      ctx.lineWidth = routeWidth(c.lanes, active);
      ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.stroke();
    }

    for (const r of s.routes) {
      const path = routePath(s, r, offsets.get(r.id) ?? 0);
      const active = ui.selection?.kind === "route" && ui.selection.id === r.id;
      const closed = r.id === closedId;

      // Warenfarbe als gestrichelte Mittellinie — verrät auf einen Blick,
      // was auf dieser Strecke transportiert wird, und trennt zwei Wege, die
      // sich einen Abschnitt teilen.
      ctx.globalAlpha = active ? 1 : 0.8;
      ctx.strokeStyle = goodColor(GOODS[r.good].token);
      ctx.lineWidth = 2.5;
      ctx.setLineDash([7, 10]);
      ctx.beginPath();
      path.forEach((q, i) => (i === 0 ? ctx.moveTo(q.x, q.y) : ctx.lineTo(q.x, q.y)));
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;

      // A closure and a jam are both red and want opposite answers — one says
      // "you bought a truck too many", the other says "trucks are not the lever
      // here". A mark rather than a fourth colour, so it composes with whatever
      // the road is already saying (RFC-0012 §9.2).
      if (closed) {
        const w = routeWidth(routeLanes(s, r), active);
        const mid = pointOnPath(path, 0.5);
        ctx.strokeStyle = p.error;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(mid.x - mid.uy * w, mid.y + mid.ux * w);
        ctx.lineTo(mid.x + mid.uy * w, mid.y - mid.ux * w);
        ctx.stroke();
      }
    }
  }

  private drawTrucks(s: GameState): void {
    const { ctx } = this;
    const p = palette();
    const offsets = routeOffsets(s);
    for (const t of s.trucks) {
      const r = findRoute(s, t.route);
      if (!r) continue;
      // The same shift the road got, or the fleet drives beside its own asphalt.
      const at = pointOnPath(routePath(s, r, offsets.get(r.id) ?? 0), t.pos);
      const outbound = t.state === "outbound" || t.state === "unloading";
      const side = outbound ? 1 : -1; // Rechtsverkehr
      const px = at.x - at.uy * 7 * side;
      const py = at.y + at.ux * 7 * side;
      const angle = Math.atan2(at.uy, at.ux) + (outbound ? 0 : Math.PI);

      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(angle);
      ctx.fillStyle = t.cargo > 0 ? goodColor(GOODS[r.good].token) : p.truckEmpty;
      this.roundRect(-11, -6, 22, 12, 3.5);
      ctx.fill();
      ctx.fillStyle = "rgba(0,0,0,0.28)";
      this.roundRect(4, -6, 7, 12, 3);
      ctx.fill();
      ctx.restore();

      // Both stalls, not just the empty store: a truck jammed in front of a full
      // hub costs the same upkeep and is the same mistake.
      if ((t.state === "loading" || t.state === "unloading") && t.idle > 0.6) {
        ctx.fillStyle = p.error;
        ctx.font = "600 16px system-ui";
        ctx.textAlign = "center";
        ctx.fillText("!", px, py - 13);
      }
    }
  }

  private drawNodes(s: GameState, ui: UiState): void {
    const { ctx } = this;
    const p = palette();
    for (const n of s.nodes) {
      const selected = ui.selection?.kind === "node" && ui.selection.id === n.id;
      const highlighted = selected || ui.hover === n.id;

      if (highlighted) {
        ctx.fillStyle = p.coral;
        ctx.globalAlpha = 0.12;
        ctx.beginPath(); ctx.arc(n.x, n.y, NODE_R + 12, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
      }

      // A refinery wears the colour of what it makes, which is the rule a
      // producer already follows — RFC-0015 §9.2, and the cheapest honest answer
      // to it: a hub that makes a good is not a new shape, it is a hub with a
      // source's colour. A plain buffer keeps the neutral one.
      const accent = n.kind === "producer"
        ? goodColor(GOODS[n.good].token)
        : n.kind === "city" ? p.city
          : n.refines ? goodColor(GOODS.werkzeug.token) : p.transfer;

      ctx.fillStyle = p.nodeFill;
      // A hub is a place, not a source or a sink — and shape reads faster than
      // colour. Hit testing stays a circle over the square; the difference is
      // smaller than the slack NODE_HIT_R already carries.
      const body = (): void => {
        if (n.kind === "transfer") this.roundRect(n.x - NODE_R, n.y - NODE_R, NODE_R * 2, NODE_R * 2, 9);
        else { ctx.beginPath(); ctx.arc(n.x, n.y, NODE_R, 0, Math.PI * 2); }
      };
      body(); ctx.fill();
      ctx.strokeStyle = accent;
      ctx.lineWidth = 2.5;
      body(); ctx.stroke();

      let fill: number;
      let ringColor: string;
      let label: string;
      if (n.kind === "producer") {
        fill = n.stock / n.cap;
        ringColor = fill > 0.96 ? p.error : accent;
        label = String(Math.floor(n.stock));
      } else if (n.kind === "transfer") {
        const total = totalStock(n);
        fill = clamp(total / n.cap, 0, 1);
        ringColor = fill > 0.96 ? p.error : accent;
        label = String(Math.floor(total));
      } else {
        // Against the cap this city can actually reach: BACKLOG_CAP is per good,
        // so a two-good city owing 200 of each is full, not double.
        const wants = wantedGoods(n);
        fill = clamp(totalBacklog(n) / (BACKLOG_CAP * Math.max(1, wants.length)), 0, 1);
        ringColor = n.trust > 0.85 ? p.success : n.trust > 0.65 ? p.warning : p.error;
        label = `${Math.round(n.trust * 100)}%`;
        // What this city eats, in the producers' own colours, so a glance at the
        // map answers "which sawmill is this town worth anything to" without
        // opening a card. Above the node, where nothing else is drawn — the ring
        // owns the edge and the name owns the space below.
        // Clear of the ring, not merely above the body: the ring is 5 wide and
        // starts at the top, so a dot placed against the node reads as a balloon
        // on a string wherever the trust arc happens to end.
        const dotY = n.y - (NODE_R + RING_GAP + GOOD_DOT_R + 5);
        wants.forEach((g, i) => {
          ctx.fillStyle = goodColor(GOODS[g].token);
          ctx.beginPath();
          ctx.arc(
            n.x + (i - (wants.length - 1) / 2) * GOOD_DOT_GAP,
            dotY, GOOD_DOT_R, 0, Math.PI * 2,
          );
          ctx.fill();
        });
      }

      ctx.strokeStyle = ringColor;
      ctx.lineWidth = 5;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.arc(n.x, n.y, NODE_R + RING_GAP, -Math.PI / 2, -Math.PI / 2 + fill * Math.PI * 2);
      ctx.stroke();

      ctx.fillStyle = accent;
      ctx.font = "500 13px system-ui";
      ctx.textAlign = "center";
      ctx.fillText(label, n.x, n.y + 5);

      ctx.fillStyle = p.muted;
      ctx.font = '400 13px ui-serif, Georgia, serif';
      ctx.fillText(n.name, n.x, n.y + NODE_R + 27);
    }
  }

  private drawFloats(ui: UiState): void {
    const { ctx } = this;
    for (const f of ui.floats) {
      ctx.globalAlpha = clamp(f.life / FLOAT_LIFETIME, 0, 1);
      ctx.fillStyle = f.color;
      ctx.font = "500 14px system-ui";
      ctx.textAlign = "center";
      ctx.fillText(f.text, f.x, f.y);
      ctx.globalAlpha = 1;
    }
  }

  // ── Primitive ────────────────────────────────────────────────────────────

  private roundRect(x: number, y: number, w: number, h: number, r: number): void {
    const { ctx } = this;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  private pill(x: number, y: number, text: string, color: string): void {
    const { ctx } = this;
    const p = palette();
    ctx.font = "500 13px system-ui";
    ctx.textAlign = "center";
    const w = ctx.measureText(text).width + 18;
    ctx.fillStyle = p.surface;
    this.roundRect(x - w / 2, y - 12, w, 24, 8);
    ctx.fill();
    ctx.strokeStyle = p.route;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = color;
    ctx.fillText(text, x, y + 4);
  }
}

/**
 * Nächstgelegener Knoten unter dem Punkt, oder null.
 *
 * Nearest, not first — which is what the name always claimed and the body did
 * not. It matters now: `radius` is widened for a finger (`src/ui/input.ts`), and
 * once two targets can both contain the point, "whichever comes first in the
 * array" is a coin toss the player cannot predict.
 *
 * @param radius Hit radius in world units. The caller widens it so the target
 *   clears 44 px on screen at whatever the camera is currently showing.
 */
export function hitNode(
  s: GameState, x: number, y: number, radius = NODE_HIT_R,
): GameNode | null {
  let best: GameNode | null = null;
  let bestDist = radius;
  for (const n of s.nodes) {
    const d = Math.hypot(n.x - x, n.y - y);
    if (d < bestDist) {
      bestDist = d;
      best = n;
    }
  }
  return best;
}

/**
 * Nearest route within `radius`, for the same reason hitNode takes one — and the
 * *next* one after `current` when several lie under the same point.
 *
 * Two goods out of one hub into one city are two roads with the same course
 * (`buildRoute()` in sim.ts allows the pair twice when the good differs). Their
 * distance to a click is identical to the bit, so a plain nearest-wins search
 * always returned whichever came first in `s.routes` and the other one could not
 * be selected by clicking at all — the road was on screen, priced, staffed, and
 * unreachable.
 *
 * Cycling is the whole fix: click the same place again and the selection steps
 * to the next road under the pointer. It costs no second gesture to learn and it
 * also covers roads that merely cross, not only ones that coincide.
 */
export function hitRoute(
  s: GameState, x: number, y: number, radius = ROUTE_HIT_R,
  current: number | null = null,
): number | null {
  const under: { id: number; d: number }[] = [];
  const offsets = routeOffsets(s);
  for (const r of s.routes) {
    // Every leg, not the chord: a road drawn through a waypoint that can only
    // be hit on the straight line between its ends lies about where it is.
    const path = routePath(s, r, offsets.get(r.id) ?? 0);
    let best = Infinity;
    for (let i = 0; i + 1 < path.length; i++) {
      const a = path[i]!, b = path[i + 1]!;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const t = clamp(((x - a.x) * dx + (y - a.y) * dy) / (dx * dx + dy * dy), 0, 1);
      best = Math.min(best, Math.hypot(a.x + dx * t - x, a.y + dy * t - y));
    }
    if (best < radius) under.push({ id: r.id, d: best });
  }
  if (under.length === 0) return null;
  // By distance, then by id — so the order a tie is walked in is the order the
  // roads were built, and it does not change under the player between clicks.
  under.sort((p, q) => p.d - q.d || p.id - q.id);
  const at = under.findIndex((h) => h.id === current);
  return under[at >= 0 ? (at + 1) % under.length : 0]!.id;
}

/** Nur exportiert, damit Tests die Geschwindigkeitsannahme prüfen können. */
export const DEBUG_TRUCK_SPEED = TRUCK_SPEED;
